import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { navigationItems } from '../mockData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (path) => {
    if (path.startsWith('/#')) {
      const section = path.substring(2);
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const element = document.getElementById(section);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        }, 100);
      } else {
        const element = document.getElementById(section);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      navigate(path);
    }
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = () => {
    if (location.pathname === '/') {
      // If already on home page, scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // If on another page, navigate to home and scroll to top
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  const handlePortfolioClick = () => {
    navigate('/portfolio');
    setIsMobileMenuOpen(false);
  };

  const handleContactClick = () => {
    if (location.pathname !== '/') {
      // If not on home page, navigate to home and then scroll to footer
      navigate('/');
      setTimeout(() => {
        const footer = document.querySelector('footer');
        if (footer) {
          footer.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If on home page, just scroll to footer
      const footer = document.querySelector('footer');
      if (footer) {
        footer.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <button
            onClick={handleHomeClick}
            className="flex items-center space-x-3 group"
          >
            <div className="w-10 h-10 bg-black  flex items-center justify-center transition-transform duration-300 group-hover:scale-110 overflow-hidden">
              <img 
                src="/logowhite.png" 
                alt="Elemento Design" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="text-white font-bold text-xl tracking-tight hidden sm:block">
              ELEMENTO DESIGN
            </span>
          </button>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <button
              onClick={handleHomeClick}
              className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium tracking-wide relative group"
            >
              Home
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
            </button>
            
            {navigationItems.slice(1).map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  if (item.name === 'Portfolio') {
                    handlePortfolioClick();
                  } else {
                    handleNavClick(item.path);
                  }
                }}
                className="text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium tracking-wide relative group"
              >
                {item.name}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full"></span>
              </button>
            ))}
            
            {/* Contact Us Button */}
            <button
              onClick={handleContactClick}
              className="bg-white text-black hover:bg-gray-200 transition-all duration-300 px-6 py-2 rounded-sm text-sm font-medium tracking-wide transform hover:scale-105"
            >
              Contact Us
            </button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden text-white hover:text-gray-300 transition-colors"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-4">
            <button
              onClick={handleHomeClick}
              className="block w-full text-left text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium tracking-wide py-2"
            >
              Home
            </button>
            
            {navigationItems.slice(1).map((item) => (
              <button
                key={item.name}
                onClick={() => {
                  if (item.name === 'Portfolio') {
                    handlePortfolioClick();
                  } else {
                    handleNavClick(item.path);
                  }
                }}
                className="block w-full text-left text-white hover:text-gray-300 transition-colors duration-200 text-sm font-medium tracking-wide py-2"
              >
                {item.name}
              </button>
            ))}
            
            {/* Contact Us Button - Mobile */}
            <button
              onClick={handleContactClick}
              className="block w-full text-left bg-white text-black hover:bg-gray-200 transition-all duration-300 px-4 py-2 rounded-sm text-sm font-medium tracking-wide"
            >
              Contact Us
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;